import { type SiteMasterCredentialSearchOption } from "~hooks/useSiteMasterCredentialsCatalog";

export interface PlatformItemCount {
  count: number;
  name: string;
  type: "skill" | "certification";
}

export interface PlatformRankInfo {
  count: number;
  rank: number;
}

function buildCredentialCatalogMaps(credentialCatalog: SiteMasterCredentialSearchOption[] | undefined) {
  const byCode = new Map<string, SiteMasterCredentialSearchOption>();
  const byTitle = new Map<string, SiteMasterCredentialSearchOption>();

  for (const item of credentialCatalog ?? []) {
    if (item.code) {
      byCode.set(item.code.toLowerCase(), item);
    }
    byTitle.set(item.title.toLowerCase(), item);
  }

  return { byCode, byTitle };
}

/** Resume certifications are often stored as "FDOECode | Certification Title". */
function certificationNameKeys(name: string): string[] {
  const keys = new Set<string>();
  const trimmed = name.trim();
  if (!trimmed) {
    return [];
  }

  keys.add(trimmed);

  const pipeIndex = trimmed.indexOf(" | ");
  if (pipeIndex >= 0) {
    const code = trimmed.slice(0, pipeIndex).trim();
    const title = trimmed.slice(pipeIndex + 3).trim();
    if (code) {
      keys.add(code);
    }
    if (title) {
      keys.add(title);
    }
  }

  return [...keys];
}

/** Maps search terms and stored profile values to platform rank/count, including cert code/title aliases. */
export function buildPlatformRankLookup(
  platformData: PlatformItemCount[],
  credentialCatalog?: SiteMasterCredentialSearchOption[],
): Map<string, PlatformRankInfo> {
  const { byCode, byTitle } = buildCredentialCatalogMaps(credentialCatalog);
  const lookup = new Map<string, PlatformRankInfo>();

  platformData.forEach((item, index) => {
    const rankInfo: PlatformRankInfo = { count: item.count, rank: index + 1 };

    const register = (type: PlatformItemCount["type"], key: string) => {
      lookup.set(`${type}:${key.toLowerCase()}`, rankInfo);
    };

    register(item.type, item.name);

    if (item.type !== "certification") {
      return;
    }

    for (const key of certificationNameKeys(item.name)) {
      register("certification", key);
    }

    const catalogMatch = certificationNameKeys(item.name)
      .map((key) => byCode.get(key.toLowerCase()) ?? byTitle.get(key.toLowerCase()))
      .find(Boolean);

    if (!catalogMatch) {
      return;
    }

    if (catalogMatch.code) {
      register("certification", catalogMatch.code);
    }
    if (catalogMatch.title) {
      register("certification", catalogMatch.title);
    }
  });

  return lookup;
}

export function findPlatformMatch(args: {
  keyword: string;
  lookup: Map<string, PlatformRankInfo>;
  type: "skill" | "certification" | "keyword";
}): PlatformRankInfo | null {
  const normalized = args.keyword.toLowerCase();

  if (args.type === "skill") {
    return args.lookup.get(`skill:${normalized}`) ?? null;
  }

  if (args.type === "certification") {
    return args.lookup.get(`certification:${normalized}`) ?? null;
  }

  return args.lookup.get(`skill:${normalized}`) ?? args.lookup.get(`certification:${normalized}`) ?? null;
}
